import { lazy, Suspense, useEffect, type ComponentType } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useNavigate, useParams, useRouteError, type RouteObject } from 'react-router';
import { RefreshCw } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { api } from './lib/api';
import { buildReviewerUrl } from '@rv/shared';
import { homeFor, isTeam, RequireAuth, useAuth, useMe } from './lib/auth';
import { Button, ErrorState, FullPageSpinner, EmptyState } from './components/ui';
import { isChunkLoadError, reloadForNewVersion, withChunkReload } from './lib/chunkReload';
import { AcceptInvitePage, ForgotPasswordPage, LoginPage, MagicLinkPage, ResetPasswordPage } from './pages/auth/AuthPages';

/** Route-level code splitting: the client portal never downloads admin screens and vice versa. */
// A screen file missing after a deploy (an old tab) reloads the page once instead of failing.
const lazyNamed = <T extends Record<string, any>>(loader: () => Promise<T>, name: keyof T) => lazy(withChunkReload(() => loader().then((m) => ({ default: m[name] as ComponentType<any> }))));
const AdminLayout = lazyNamed(() => import('./shells/AdminLayout'), 'AdminLayout');
const PortalLayout = lazyNamed(() => import('./shells/PortalLayout'), 'PortalLayout');
const DashboardPage = lazyNamed(() => import('./pages/admin/Dashboard'), 'DashboardPage');
const ProjectsPage = lazyNamed(() => import('./pages/admin/Projects'), 'ProjectsPage');
const ProjectDetailPage = lazyNamed(() => import('./pages/admin/ProjectDetail'), 'ProjectDetailPage');
const UploadWizardPage = lazyNamed(() => import('./pages/admin/UploadWizard'), 'UploadWizardPage');
const NewProjectPage = lazyNamed(() => import('./pages/admin/NewProject'), 'NewProjectPage');
const InboxPage = lazyNamed(() => import('./pages/admin/Inbox'), 'InboxPage');
const QABoardPage = lazyNamed(() => import('./pages/admin/Inbox'), 'QABoardPage');
const PickListsPage = lazyNamed(() => import('./pages/admin/PickLists'), 'PickListsPage');
const IntegrationsPage = lazyNamed(() => import('./pages/admin/Integrations'), 'IntegrationsPage');
const ShopifyAppsPage = lazyNamed(() => import('./pages/admin/ShopifyStores'), 'ShopifyAppsPage');
const IntegrationDetailPage = lazyNamed(() => import('./pages/admin/Integrations'), 'IntegrationDetailPage');
const AccountPage = lazyNamed(() => import('./pages/admin/AdminMisc'), 'AccountPage');
const ApprovalsPage = lazyNamed(() => import('./pages/admin/AdminMisc'), 'ApprovalsPage');
const GlobalActivityPage = lazyNamed(() => import('./pages/admin/AdminMisc'), 'GlobalActivityPage');
const SettingsPage = lazyNamed(() => import('./pages/admin/AdminMisc'), 'SettingsPage');
const UsersPage = lazyNamed(() => import('./pages/admin/AdminMisc'), 'UsersPage');
const RolesPage = lazyNamed(() => import('./pages/admin/RolesPage'), 'RolesPage');
const AccessRequestsPage = lazyNamed(() => import('./pages/admin/AccessRequests'), 'AccessRequestsPage');
const MyCommentsPage = lazyNamed(() => import('./pages/portal/PortalPages'), 'MyCommentsPage');
const PickListIndexPage = lazyNamed(() => import('./pages/portal/PortalPages'), 'PickListIndexPage');
const PickListPage = lazyNamed(() => import('./pages/portal/PortalPages'), 'PickListPage');
const PortalProjectPage = lazyNamed(() => import('./pages/portal/PortalPages'), 'PortalProjectPage');
const PortalProjectsPage = lazyNamed(() => import('./pages/portal/PortalPages'), 'PortalProjectsPage');
const ReviewsPage = lazyNamed(() => import('./pages/portal/PortalPages'), 'ReviewsPage');
const Workspace = lazyNamed(() => import('./review/Workspace'), 'Workspace');
const LiveWorkspace = lazyNamed(() => import('./review/LiveWorkspace'), 'LiveWorkspace');
const ComparePage = lazyNamed(() => import('./review/Compare'), 'ComparePage');


function Home() {
  const me = useMe();
  if (me.isLoading) return <FullPageSpinner />;
  return <Navigate to={homeFor(me.data)} replace />;
}

/** /issue/:id → the workspace for its latest occurrence (saved revision or live round). */
function IssueRedirect() {
  const { id = '' } = useParams();
  const me = useAuth();
  const nav = useNavigate();
  const q = useQuery({ queryKey: ['issue', id], queryFn: () => api(`/issues/${id}`) });
  useEffect(() => {
    if (!q.data) return;
    const area = isTeam(me) ? 'admin' : 'portal';
    const a = q.data.issue.anchor;
    if (a?.revisionId) nav(`/${area}/review/${a.revisionId}?issue=${id}`, { replace: true });
    else if (a?.roundId) {
      api(`/rounds/${a.roundId}`).then((r) => nav(`/${area}/live/${r.round.integrationId}`, { replace: true }));
    }
  }, [q.data]);
  if (q.isError) return <div className="p-6"><ErrorState error={q.error} title="This comment is not available" /></div>;
  return <FullPageSpinner />;
}

/** Permanent project link: resolves to the latest PUBLISHED target (never a draft). */
function PermanentLink() {
  const { key = '' } = useParams();
  const me = useAuth();
  const nav = useNavigate();
  const q = useQuery({ queryKey: ['permalink', key], queryFn: () => api(`/r/${encodeURIComponent(key)}/resolve`) });
  useEffect(() => {
    if (!q.data) return;
    const area = isTeam(me) ? 'admin' : 'portal';
    if (q.data.target?.kind === 'revision') nav(`/${area}/review/${q.data.target.revisionId}`, { replace: true });
    else nav(`/${area}/projects/${q.data.projectId}`, { replace: true });
  }, [q.data]);
  if (q.isError) return <div className="p-6"><ErrorState error={q.error} title="This review link is not available to you" /></div>;
  return <FullPageSpinner />;
}

/**
 * Review link (/r/<key>_review): public — forwards to the project's live website, where the
 * snippet then shows "Log in to comment" to this browser (and only to reviewers' browsers).
 */
function ReviewLinkRedirect({ projectKey }: { projectKey: string }) {
  // Shopify app projects: ?store=<name> opens the app in that store's admin.
  const store = new URLSearchParams(window.location.search).get('store') ?? '';
  const q = useQuery({ queryKey: ['review-link', projectKey, store], queryFn: () => api(`/review-links/${encodeURIComponent(projectKey)}${store ? `?store=${encodeURIComponent(store)}` : ''}`), retry: false });
  useEffect(() => {
    if (q.data?.url) window.location.replace(buildReviewerUrl(q.data.url));
  }, [q.data]);
  if (q.isError) return <div className="p-6"><ErrorState error={q.error} title="This review link is not available" /></div>;
  return <FullPageSpinner />;
}

/** /r/<key>: the permanent (portal) link; /r/<key>_review: the website review link. */
function ProjectLink() {
  const { key = '' } = useParams();
  if (key.endsWith('_review')) return <ReviewLinkRedirect projectKey={key.slice(0, -'_review'.length)} />;
  return (
    <RequireAuth>
      <PermanentLink />
    </RequireAuth>
  );
}

/** Instead of React Router's developer screen: a new version reloads; anything else offers "Reload". */
function RouteError() {
  const error = useRouteError();
  const newVersion = isChunkLoadError(error);
  useEffect(() => {
    if (newVersion) reloadForNewVersion();
  }, [newVersion]);
  return (
    <div className="flex min-h-screen items-center justify-center bg-canvas p-6">
      <div className="w-full max-w-md rounded-2xl border border-line bg-white p-6 text-center shadow-card" role="alert">
        <h1 className="text-[17px] font-semibold text-ink">{newVersion ? 'A new version is available' : 'Something went wrong'}</h1>
        <p className="mt-2 text-[13px] text-slate-600">{newVersion ? 'The portal was just updated. Reload the page to continue — nothing you saved is lost.' : 'This screen could not be shown. Reload the page; if it keeps happening, tell the design team.'}</p>
        {!newVersion && error instanceof Error && error.message && <p className="mt-2 break-words font-mono text-[11px] text-slate-400">{error.message.slice(0, 200)}</p>}
        <div className="mt-4 flex justify-center gap-2">
          <Button variant="primary" icon={<RefreshCw className="size-4" />} onClick={() => location.reload()}>Reload page</Button>
          <Button onClick={() => location.assign('/')}>Go to start</Button>
        </div>
      </div>
    </div>
  );
}

const withErrors = (routes: RouteObject[]): RouteObject[] => routes.map((r) => ({ errorElement: <RouteError />, ...r }));

function NotFound() {
  return <div className="p-10"><EmptyState title="Page not found" body="The page you are looking for does not exist or you don't have access." /></div>;
}

const router = createBrowserRouter(withErrors([
  { path: '/', element: <Home /> },
  { path: '/login', element: <LoginPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/invite', element: <AcceptInvitePage /> },
  { path: '/magic', element: <MagicLinkPage /> },
  { path: '/issue/:id', element: <RequireAuth><IssueRedirect /></RequireAuth> },
  { path: '/r/:key', element: <ProjectLink /> },
  { path: '/admin/review/:revisionId', element: <RequireAuth area="admin"><Workspace area="admin" /></RequireAuth> },
  { path: '/admin/live/:integrationId', element: <RequireAuth area="admin"><LiveWorkspace area="admin" /></RequireAuth> },
  { path: '/admin/compare', element: <RequireAuth area="admin"><ComparePage area="admin" /></RequireAuth> },
  { path: '/portal/compare', element: <RequireAuth><ComparePage area="portal" /></RequireAuth> },
  { path: '/portal/review/:revisionId', element: <RequireAuth><Workspace area="portal" /></RequireAuth> },
  { path: '/portal/live/:integrationId', element: <RequireAuth><LiveWorkspace area="portal" /></RequireAuth> },
  {
    path: '/admin',
    element: <RequireAuth area="admin"><AdminLayout /></RequireAuth>,
    children: [
      { index: true, element: <DashboardPage /> },
      { path: 'projects', element: <ProjectsPage /> },
      { path: 'projects/new', element: <NewProjectPage /> },
      { path: 'projects/:id', element: <ProjectDetailPage /> },
      { path: 'projects/:id/upload', element: <UploadWizardPage /> },
      { path: 'inbox', element: <InboxPage /> },
      { path: 'qa', element: <QABoardPage /> },
      { path: 'picklists', element: <PickListsPage /> },
      { path: 'integrations', element: <IntegrationsPage /> },
      { path: 'shopify-apps', element: <ShopifyAppsPage /> },
      { path: 'shopify-apps/new', element: <NewProjectPage shopify /> },
      { path: 'shopify-apps/:id', element: <ProjectDetailPage /> },
      { path: 'integrations/:id', element: <IntegrationDetailPage /> },
      { path: 'approvals', element: <ApprovalsPage area="admin" /> },
      { path: 'users', element: <UsersPage /> },
      { path: 'roles', element: <RolesPage /> },
      { path: 'requests', element: <AccessRequestsPage /> },
      { path: 'settings', element: <SettingsPage /> },
      { path: 'activity', element: <GlobalActivityPage /> },
      { path: 'account', element: <AccountPage /> },
      { path: '*', element: <NotFound /> },
    ],
  },
  {
    path: '/portal',
    element: <RequireAuth area="portal"><PortalLayout /></RequireAuth>,
    children: [
      { index: true, element: <PortalProjectsPage /> },
      { path: 'projects/:id', element: <PortalProjectPage /> },
      { path: 'reviews', element: <ReviewsPage /> },
      { path: 'comments', element: <MyCommentsPage /> },
      { path: 'picklist', element: <PickListIndexPage /> },
      { path: 'picklist/:projectId', element: <PickListPage /> },
      { path: 'approvals', element: <ApprovalsPage area="portal" /> },
      { path: 'account', element: <AccountPage /> },
      { path: '*', element: <NotFound /> },
    ],
  },
  { path: '*', element: <NotFound /> },
]));

export function App() {
  return (
    <Suspense fallback={<FullPageSpinner />}>
      <RouterProvider router={router} />
    </Suspense>
  );
}
